import React, { useCallback, useMemo, useRef } from 'react';

import { Box, Container, TextField, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import ApaDpiaStatusBadge from '../_shared/ApaDpiaStatusBadge/ApaDpiaStatusBadge';
import { APA_MULTILINE_MAX_ROWS, ApaReadField } from '../_shared/AssetProcessingApaSections/ApaReadField';
import {
  AssetProcessingCountryRowsSection,
  AssetProcessingDpiaLogSection,
  AssetProcessingDpiaSection,
  AssetProcessingPurposeApplicationsSection,
  AssetProcessingSecuritySection,
} from '../_shared/AssetProcessingApaSections/AssetProcessingApaSections';
import { resolveAssetProcessingDpiaLog } from '../_shared/assetProcessingDpiaLog.helpers';
import { canAccessOwnerApaEditorPage, canAccessOwnerAssetRopaPage, canEditDpiaNeedAssessment } from '../_shared/assetRopaReviewAccess';
import DpiaLogRecordActions from '../_shared/DpiaLogRecordActions';
import ProcessingActivitySidePanel from '../_shared/ProcessingActivitySidePanel/ProcessingActivitySidePanel';
import usePublishedProcessingActivityDetail from '../_shared/ProcessingActivitySidePanel/usePublishedProcessingActivityDetail';
import QueryUnavailableState from '../_shared/QueryUnavailableState';
import useDownloadAssetProcessingDpiaLog from '../_shared/useDownloadAssetProcessingDpiaLog';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import {
  getAssetProcessingActivitySaveValidationErrors,
  hasValidationErrors,
  isAssetProcessingActivityInactiveReviewRound,
  isAssetProcessingActivityRevisionConflict,
  isAssetProcessingActivityRevisionValidationError,
  mapProcessingActivityToSidePanel,
  validateAssetProcessingActivityForSubmit,
  validatePurposeJustificationsForSave,
} from './assetProcessingActivityEditor.helpers';
import useAssetProcessingActivityEditor from './useAssetProcessingActivityEditor';

type RouteParams = {
  apaId: string;
  assetRopaId: string;
};

type LocationState = {
  mode?: 'view' | 'edit';
  adminReadOnly?: boolean;
  returnPath?: string;
};

const AssetProcessingActivityEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { apaId, assetRopaId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessOwnerApaEditorPage(userData);
  const canEditAsOwner = canAccessOwnerAssetRopaPage(userData) && !location.state?.adminReadOnly;
  const messages = processing_activities_messages.asset_ropa.apa_editor;
  const clarificationResponseInputRef = useRef<HTMLInputElement>(null);
  const downloadDpiaLogMutation = useDownloadAssetProcessingDpiaLog();
  const dpiaLogMessages = processing_activities_messages.asset_ropa.apa_editor.sections.dpia_log;

  const navigateToList = useCallback(() => {
    history.push(location.state?.returnPath ?? (canEditAsOwner ? PATHS.PROCESSING_ACTIVITIES_ASSET_ROPA : PATHS.PROCESSING_ACTIVITIES));
  }, [canEditAsOwner, history, location.state?.returnPath]);

  const {
    clarificationResponse,
    detail,
    formValues,
    handleClarificationResponseChange,
    handleFormChange,
    handleNavigation,
    handleSubmit,
    isDirty,
    isEditing,
    isError,
    isLoading,
    isUnavailable,
    NavigationPrompt,
    readOnly,
    refetch,
    saveMutation,
    setIsEditing,
    setValidationErrors,
    submitMutation,
    validationErrors,
  } = useAssetProcessingActivityEditor({
    apaId: Number(apaId),
    assetRopaId: Number(assetRopaId),
    initialEditMode: canEditAsOwner && location.state?.mode === 'edit',
    onSaveSuccess: (_detail, closeAfterSave) => {
      enqueueSnackbar(t(messages.toasts.saved), { variant: 'success' });
      if (closeAfterSave) navigateToList();
    },
    onSubmitSuccess: () => {
      enqueueSnackbar(t(messages.toasts.submitted), { variant: 'success' });
      navigateToList();
    },
    onConflict: () => {
      enqueueSnackbar(t(messages.toasts.conflict), { variant: 'warning' });
    },
    onRevisionIntegrationError: () => {
      enqueueSnackbar(t(messages.toasts.revision_missing), { variant: 'error' });
    },
    onNavigate: navigateToList,
    organizationId,
  });
  const resolvedDpiaLog = resolveAssetProcessingDpiaLog(detail);

  const publishedActivityQuery = usePublishedProcessingActivityDetail(organizationId, detail?.processingActivity.id);

  const sidePanelActivity = useMemo(
    () => (detail ? mapProcessingActivityToSidePanel(detail, publishedActivityQuery.data) : null),
    [detail, publishedActivityQuery.data],
  );

  usePageTitle(detail?.processingActivity?.name ?? t(messages.page_title));

  const validationMessages = useMemo(
    () => ({
      required: t(validation_messages.required),
      purposeNotAssessed: t(messages.validation.purpose_not_assessed),
      purposeAppliesIncomplete: t(messages.validation.purpose_applies_incomplete),
      purposeDoesNotApplyJustification: t(messages.validation.purpose_does_not_apply_justification),
      securityIncomplete: t(messages.validation.security_incomplete),
      securityCommentRequired: t(messages.validation.security_comment_required),
      countryRowInvalid: t(messages.validation.country_row_invalid),
      countriesRequired: t(messages.validation.countries_required),
      dpiaCommentRequired: t(messages.validation.dpia_comment_required),
      retentionValueNegative: t(processing_activities_messages.editor.validation.retention_value_negative),
      retentionValueInteger: t(processing_activities_messages.editor.validation.retention_value_integer),
      clarificationResponse: t(messages.validation.clarification_response),
    }),
    [messages, t],
  );

  const handleSaveClick = useCallback(
    async (closeAfterSave: boolean) => {
      if (!formValues) return;

      const justificationErrors = validatePurposeJustificationsForSave(formValues, validationMessages);
      setValidationErrors(justificationErrors);

      if (hasValidationErrors(justificationErrors)) {
        enqueueSnackbar(t(messages.validation.submit_blocked), { variant: 'warning' });
        return;
      }

      try {
        await saveMutation.mutateAsync(closeAfterSave);
      } catch (error) {
        if (isAssetProcessingActivityRevisionConflict(error) || isAssetProcessingActivityRevisionValidationError(error)) {
          return;
        }

        const fieldErrors = getAssetProcessingActivitySaveValidationErrors(error);
        if (hasValidationErrors(fieldErrors)) {
          setValidationErrors(fieldErrors);
          enqueueSnackbar(t(messages.validation.submit_blocked), { variant: 'warning' });
          return;
        }

        enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
      }
    },
    [enqueueSnackbar, formValues, messages, saveMutation, setValidationErrors, t, validationMessages],
  );

  const handleSubmitClick = useCallback(async () => {
    if (!formValues) return;

    const errors = validateAssetProcessingActivityForSubmit(formValues, validationMessages, detail?.review?.status, clarificationResponse);
    setValidationErrors(errors);

    if (hasValidationErrors(errors)) {
      if (!isEditing) {
        setIsEditing(true);
        if (errors.clarificationResponse) {
          window.setTimeout(() => {
            clarificationResponseInputRef.current?.focus();
          }, 0);
        }
      }
      enqueueSnackbar(t(messages.validation.submit_blocked), { variant: 'warning' });
      return;
    }

    try {
      await handleSubmit();
    } catch (error) {
      if (isAssetProcessingActivityRevisionConflict(error) || isAssetProcessingActivityRevisionValidationError(error)) {
        return;
      }

      if (isAssetProcessingActivityInactiveReviewRound(error)) {
        enqueueSnackbar(t(messages.toasts.inactive_review_round), { variant: 'error' });
        return;
      }

      const fieldErrors = getAssetProcessingActivitySaveValidationErrors(error);
      if (hasValidationErrors(fieldErrors)) {
        setValidationErrors(fieldErrors);
        enqueueSnackbar(t(messages.validation.submit_blocked), { variant: 'warning' });
        return;
      }

      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [
    clarificationResponse,
    detail?.review?.status,
    enqueueSnackbar,
    formValues,
    handleSubmit,
    isEditing,
    messages,
    setIsEditing,
    setValidationErrors,
    t,
    validationMessages,
  ]);

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.owner_forbidden_body)}
      </Typography>
    );
  } else if (isUnavailable) {
    content = <QueryUnavailableState backLabel={t(general_messages.back)} message={t(messages.errors.not_found)} onBack={navigateToList} />;
  } else if (isLoading || !formValues || !detail || !sidePanelActivity) {
    content = isError ? (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <div className={styles.centeredPageActions}>
          <ColoredButton
            className=''
            customColor='grey'
            disabled={false}
            icon={false}
            onClick={() => refetch()}
            showLoader={false}
            textLike={false}
            variant='outlined'
          >
            {t(general_messages.retry)}
          </ColoredButton>
        </div>
      </Box>
    ) : (
      <Loader inner />
    );
  } else {
    const canEdit = canEditAsOwner && detail.review?.status !== 'submitted' && detail.review?.status !== 'accepted';
    const canSubmit =
      canEditAsOwner && (detail.review?.status === 'awaiting_response' || detail.review?.status === 'clarification_requested');
    const clarificationComment = detail.review?.clarificationComment;
    const isClarificationRequested = detail.review?.status === 'clarification_requested';
    const showClarification = Boolean(clarificationComment) || isClarificationRequested;

    const handleClarifyClick = () => {
      setIsEditing(true);
      window.setTimeout(() => {
        clarificationResponseInputRef.current?.focus();
      }, 0);
    };

    content = (
      <>
        {detail.dpiaDisplayStatus && (
          <Box className={styles.reviewMeta} mb={2}>
            <ApaDpiaStatusBadge
              dpiaLifecycleStatus={detail.dpiaDisplayStatus === 'draft' ? 'draft' : null}
              dpiaRequestStatus={detail.dpiaRequestStatus}
              status={detail.dpiaDisplayStatus}
            />
          </Box>
        )}

        {showClarification && (
          <>
            {clarificationComment && (
              <Box className={styles.reviewBanner}>
                <div className={styles.reviewBannerRow}>
                  <Typography variant='body2'>
                    <strong>{t(messages.clarification_comment)}:</strong> {clarificationComment}
                  </Typography>
                  {!isEditing && canEdit && isClarificationRequested && (
                    <TextButton onClick={handleClarifyClick}>{t(messages.clarify)}</TextButton>
                  )}
                </div>
              </Box>
            )}
            {isClarificationRequested && readOnly && Boolean(clarificationResponse) && (
              <ApaReadField label={t(messages.clarification_response)} value={clarificationResponse} />
            )}
            {isClarificationRequested && !readOnly && (
              <TextField
                error={Boolean(validationErrors.clarificationResponse)}
                fullWidth
                helperText={validationErrors.clarificationResponse}
                inputRef={clarificationResponseInputRef}
                label={t(messages.clarification_response)}
                margin='normal'
                maxRows={APA_MULTILINE_MAX_ROWS}
                minRows={4}
                multiline
                onChange={event => handleClarificationResponseChange(event.target.value)}
                value={clarificationResponse}
                variant='outlined'
              />
            )}
          </>
        )}
        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(messages.truth_note)}
        </Typography>
        <div className={styles.twoColumnLayout}>
          <ProcessingActivitySidePanel activity={sidePanelActivity} />
          <section className={styles.mainPanel}>
            <AssetProcessingSecuritySection errors={validationErrors} onChange={handleFormChange} readOnly={readOnly} values={formValues} />
            <AssetProcessingPurposeApplicationsSection
              centralPurposes={sidePanelActivity.purposes}
              errors={validationErrors}
              onChange={handleFormChange}
              readOnly={readOnly}
              values={formValues}
            />
            <AssetProcessingCountryRowsSection
              errors={validationErrors}
              onChange={handleFormChange}
              readOnly={readOnly}
              values={formValues}
            />
            {readOnly && resolvedDpiaLog ? (
              <AssetProcessingDpiaLogSection
                downloadDisabled={!resolvedDpiaLog.canDownload}
                downloadDisabledReason={t(dpiaLogMessages.download_unavailable)}
                downloading={downloadDpiaLogMutation.isLoading}
                fileName={resolvedDpiaLog.log.fileName}
                onDownload={() => {
                  if (!organizationId || !detail || !resolvedDpiaLog.canDownload) return;

                  downloadDpiaLogMutation.mutate({
                    assetProcessingActivityId: detail.id,
                    fallbackFileName: resolvedDpiaLog.log.fileName,
                    organizationId,
                  });
                }}
                recordActions={
                  detail && canEditDpiaNeedAssessment(userData) ? (
                    <DpiaLogRecordActions apaId={detail.id} organizationId={organizationId} requestStatus={detail.dpiaRequestStatus} />
                  ) : undefined
                }
              />
            ) : (
              <AssetProcessingDpiaSection errors={validationErrors} onChange={handleFormChange} readOnly={readOnly} values={formValues} />
            )}
            <div className={styles.pageActions}>
              {!isEditing && canEdit && (
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={false}
                  icon={false}
                  onClick={() => setIsEditing(true)}
                  showLoader={false}
                  textLike={false}
                  variant='contained'
                >
                  {t(messages.edit)}
                </ColoredButton>
              )}
              {isEditing && (
                <>
                  <ColoredButton
                    className=''
                    customColor='secondary'
                    disabled={saveMutation.isLoading || !isDirty}
                    icon={false}
                    onClick={() => handleSaveClick(false)}
                    showLoader={saveMutation.isLoading}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(messages.save)}
                  </ColoredButton>
                  <ColoredButton
                    className=''
                    customColor='secondary'
                    disabled={saveMutation.isLoading || !isDirty}
                    icon={false}
                    onClick={() => handleSaveClick(true)}
                    showLoader={false}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(messages.save_close)}
                  </ColoredButton>
                </>
              )}
              {canSubmit && (
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={submitMutation.isLoading || saveMutation.isLoading}
                  icon={false}
                  onClick={handleSubmitClick}
                  showLoader={submitMutation.isLoading}
                  textLike={false}
                  variant='contained'
                >
                  {t(isClarificationRequested ? messages.submit_clarification_response : messages.submit)}
                </ColoredButton>
              )}
            </div>
          </section>
        </div>
        <NavigationPrompt />
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader backButton noBorder onBack={() => handleNavigation()} subtitle={t(messages.page_subtitle)}>
          {detail?.processingActivity?.name || t(messages.page_title)}
        </ManagePageHeader>
        {content}
      </Container>
    </ErrorBoundary>
  );
};

export default AssetProcessingActivityEditorPage;
